import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Eye, EyeOff, Loader2, Check, X, Mail } from 'lucide-react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'

type Role = 'user' | 'supporter'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

export function SignUpPage() {
  const { signUp, isLoading, error, clearError } = useAuthStore()
  const turnstileRef = useRef<TurnstileInstance>(null)
  const emailRef = useRef('')
  const passwordRef = useRef('')
  const roleRef = useRef<Role>('user')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [role, setRole] = useState<Role>('user')
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [showEmailSent, setShowEmailSent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)

  // Keep refs in sync with state so callbacks always have current values
  emailRef.current = email
  passwordRef.current = password
  roleRef.current = role

  // Password validation checks
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  }

  const allPasswordChecks = Object.values(passwordChecks).every(Boolean)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setFormError(null)

    // Validation
    if (!email.trim()) {
      setFormError('Email is required')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFormError('Please enter a valid email address')
      return
    }
    if (!password) {
      setFormError('Password is required')
      return
    }
    if (!allPasswordChecks) {
      setFormError('Password does not meet requirements')
      return
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      return
    }

    // If we already have a token, submit immediately.
    // Otherwise, trigger Turnstile — onSuccess will call doSignUp.
    if (captchaToken) {
      await doSignUp(captchaToken)
    } else {
      turnstileRef.current?.execute()
    }
  }

  const doSignUp = async (token: string) => {
    const { error } = await signUp(emailRef.current, passwordRef.current, roleRef.current, token)

    if (error) {
      setCaptchaToken(null)
      turnstileRef.current?.reset()
    } else {
      setShowEmailSent(true)
    }
  }

  if (showEmailSent) {
    return (
      <div
        className="bg-[#FBF9F5] rounded-3xl p-8 border border-[#E3DED2] text-center"
        style={{ boxShadow: '0 6px 18px rgba(15, 28, 46, 0.08)' }}
      >
        <div className="w-16 h-16 bg-[#6B9D7A]/10 rounded-full flex items-center justify-center mx-auto mb-5">
          <Mail size={28} className="text-[#6B9D7A]" />
        </div>
        <h1 className="text-[30px] font-bold text-[#0F1C2E] mb-2">Check your email</h1>
        <p className="text-[#6E7582] mb-4">
          We've sent a verification link to <strong className="text-[#0F1C2E]">{email}</strong>
        </p>
        <p className="text-sm text-[#A4A9B3] mb-8">
          Click the link in the email to verify your account and complete signup.
          If you don't see it within a minute or two, check your spam folder too.
        </p>
        <Link
          to="/auth/sign-in"
          className="text-[#4A6484] hover:underline transition-colors"
        >
          Return to Log In
        </Link>
      </div>
    )
  }

  return (
    <div
      className="bg-[#FBF9F5] rounded-3xl p-8 border border-[#E3DED2]"
      style={{ boxShadow: '0 6px 18px rgba(15, 28, 46, 0.08)' }}
    >
      <h1 className="text-[30px] font-bold text-[#0F1C2E] mb-2">Create Account</h1>
      <p className="text-[#6E7582] text-base mb-8">Start your journey to driving without fear</p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Role selection */}
        <div>
          <label className="block text-sm font-medium text-[#6E7582] mb-3">
            I am a...
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setRole('user')}
              className={`flex-1 py-4 px-4 rounded-3xl border-2 transition-colors text-left ${
                role === 'user'
                  ? 'border-[#D4AF5A] bg-[#FBF9F5]'
                  : 'border-[#E3DED2] bg-[#FBF9F5] hover:border-[#A4A9B3]'
              }`}
            >
              <span className={`font-semibold text-sm ${role === 'user' ? 'text-[#0F1C2E]' : 'text-[#0F1C2E]'}`}>
                Client
              </span>
              <p className="text-xs text-[#6E7582] mt-1">I need help with driving anxiety</p>
            </button>
            <button
              type="button"
              onClick={() => setRole('supporter')}
              className={`flex-1 py-4 px-4 rounded-3xl border-2 transition-colors text-left ${
                role === 'supporter'
                  ? 'border-[#D4AF5A] bg-[#FBF9F5]'
                  : 'border-[#E3DED2] bg-[#FBF9F5] hover:border-[#A4A9B3]'
              }`}
            >
              <span className={`font-semibold text-sm ${role === 'supporter' ? 'text-[#0F1C2E]' : 'text-[#0F1C2E]'}`}>
                Support Person
              </span>
              <p className="text-xs text-[#6E7582] mt-1">I want to support someone (supporter, family, friend)</p>
            </button>
          </div>
        </div>

        {/* Email field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-[#6E7582] mb-2">
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3.5 bg-[#FBF9F5] border border-[#E3DED2] rounded-2xl text-[#0F1C2E] placeholder-[#A4A9B3] outline-none transition-colors focus:border-[#4A6484] focus:ring-2 focus:ring-[#4A6484]/20"
            placeholder="you@example.com"
            disabled={isLoading}
            autoComplete="email"
          />
        </div>

        {/* Password field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-[#6E7582] mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3.5 pr-12 bg-[#FBF9F5] border border-[#E3DED2] rounded-2xl text-[#0F1C2E] placeholder-[#A4A9B3] outline-none transition-colors focus:border-[#4A6484] focus:ring-2 focus:ring-[#4A6484]/20"
              placeholder="Create a password"
              disabled={isLoading}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6E7582] hover:text-[#0F1C2E] transition-colors"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Password requirements */}
          {password.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <PasswordCheck passed={passwordChecks.length} text="At least 8 characters" />
              <PasswordCheck passed={passwordChecks.uppercase} text="One uppercase letter" />
              <PasswordCheck passed={passwordChecks.lowercase} text="One lowercase letter" />
              <PasswordCheck passed={passwordChecks.number} text="One number" />
            </div>
          )}
        </div>

        {/* Confirm password field */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#6E7582] mb-2">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-3.5 bg-[#FBF9F5] border border-[#E3DED2] rounded-2xl text-[#0F1C2E] placeholder-[#A4A9B3] outline-none transition-colors focus:border-[#4A6484] focus:ring-2 focus:ring-[#4A6484]/20"
            placeholder="Confirm your password"
            disabled={isLoading}
            autoComplete="new-password"
          />
          {confirmPassword && password !== confirmPassword && (
            <p className="mt-2 text-sm text-[#C7796B]">Passwords do not match</p>
          )}
        </div>

        {/* Cloudflare Turnstile (invisible — triggered on submit) */}
        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_SITE_KEY}
          onSuccess={(token) => {
            setCaptchaToken(token)
            setFormError(null)
            doSignUp(token)
          }}
          onError={() => {
            setCaptchaToken(null)
            setFormError('Verification failed. Please try again.')
          }}
          onExpire={() => {
            setCaptchaToken(null)
          }}
          options={{ theme: 'light', size: 'invisible', execution: 'execute' }}
        />

        {/* Error message */}
        {(formError || error) && (
          <div className="p-3 bg-[#C7796B]/10 border border-[#C7796B] rounded-2xl text-[#C7796B] text-sm">
            {formError || error}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-[52px] bg-[#D4AF5A] text-[#0F1C2E] font-semibold rounded-full hover:bg-[#C09A4E] focus:ring-2 focus:ring-[#D4AF5A]/40 focus:ring-offset-2 focus:ring-offset-[#FBF9F5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Creating account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      {/* Terms notice */}
      <p className="mt-5 text-xs text-[#A4A9B3] text-center leading-relaxed">
        By creating an account, you agree to our{' '}
        <Link to="/terms" className="text-[#4A6484] hover:underline">Terms of Service</Link>
        {' '}and{' '}
        <Link to="/privacy" className="text-[#4A6484] hover:underline">Privacy Policy</Link>
      </p>

      {/* Sign in link */}
      <div className="mt-8 pt-6 border-t border-[#E3DED2] text-center">
        <p className="text-[#6E7582]">
          Already have an account?{' '}
          <Link to="/auth/sign-in" className="text-[#D4AF5A] font-medium hover:text-[#C09A4E] transition-colors">
            Log in
          </Link>
        </p>
      </div>
    </div>
  )
}

function PasswordCheck({ passed, text }: { passed: boolean; text: string }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${passed ? 'text-[#6B9D7A]' : 'text-[#A4A9B3]'}`}>
      {passed ? <Check size={14} /> : <X size={14} />}
      <span>{text}</span>
    </div>
  )
}
